"use client";

import { useCallback, useEffect, useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import type { AddressSearchOutput } from "@tillix/api-contract/client-schemas";
import { useHydrateServiceAccounts } from "@tillix/tx-core/react";
import { untracked } from "mobx";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import AccountLookupResults from "@/features/account-lookup/AccountLookupResults";
import AccountLookupTabs from "@/features/account-lookup/account-lookup-tabs";
import { extractAccountFields } from "@/features/account-lookup/utils/extract-account-fields";
import { useT } from "@/features/i18n/hooks/use-t";
import AccountField, { FIELD_CONFIGS } from "../../fields/account-field";
import type { AddServiceAccountFormRenderProps } from "../../forms/add-service-account.form-variants";
import type { HCAddServiceAccountFormValues } from "../schemas/hc.add-service-account";

function HCAddServiceAccountForm({
  closeDialog,
  defaultValues,
  onAccountCreated,
  onExpandChange,
  schema,
  tenantId,
}: AddServiceAccountFormRenderProps) {
  const { t } = useT();
  const serviceAccountStore = useHydrateServiceAccounts();
  const [tabValue, setTabValue] = useState<string | null>("account");
  const [addressResults, setAddressResults] = useState<
    AddressSearchOutput["results"] | null
  >(null);

  const hasResults = addressResults !== null && addressResults.length > 0;

  useEffect(() => {
    onExpandChange?.(hasResults);
  }, [hasResults, onExpandChange]);

  const form = useForm<HCAddServiceAccountFormValues>({
    resolver: arktypeResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    formState: { isSubmitting, isValid, errors },
  } = form;
  const rootError = errors.root?.message;

  const handleAddressAccountSelected = useCallback(
    async (account: AddressSearchOutput["results"][number]) => {
      const { externalId, nameOnAccount, zipCode } =
        extractAccountFields(account);
      try {
        const created = await serviceAccountStore.createServiceAccount({
          nickname: "",
          externalId,
          meta: { nameOnAccount, zipCode },
        });
        onAccountCreated?.(created.id);
        form.reset(defaultValues);
        closeDialog();
      } catch (err) {
        console.error("failed to add service account", err);
        form.setError("root", {
          message:
            err instanceof Error
              ? err.message
              : t("accounts.couldNotAddAccount"),
        });
        setAddressResults(null);
      }
    },
    [
      serviceAccountStore,
      onAccountCreated,
      form,
      defaultValues,
      closeDialog,
      t,
    ],
  );

  const handleSearchResults = useCallback(
    (results: AddressSearchOutput["results"]) => {
      if (results.length === 1) {
        void handleAddressAccountSelected(results[0]);
      } else {
        setAddressResults(results);
      }
    },
    [handleAddressAccountSelected],
  );

  const handleSubmit = async (values: HCAddServiceAccountFormValues) => {
    const trimmed = values.nickname?.trim();
    if (trimmed) {
      const isDuplicate = untracked(() =>
        serviceAccountStore.data.some(
          (sa) =>
            sa.nickname != null &&
            sa.nickname.toLowerCase() === trimmed.toLowerCase(),
        ),
      );
      if (isDuplicate) {
        form.setError("nickname", {
          message: t("onboarding.duplicateNickname"),
        });
        return;
      }
    }

    try {
      const created = await serviceAccountStore.createServiceAccount({
        nickname: values.nickname?.trim() || "",
        externalId: values.externalId.trim(),
        meta: {
          nameOnAccount: values.nameOnAccount.trim(),
          zipCode: values.zipCode.trim(),
        },
      });
      onAccountCreated?.(created.id);
      form.reset(defaultValues);
      closeDialog();
    } catch (err) {
      console.error("failed to add service account", err);
      form.setError("root", {
        message:
          err instanceof Error ? err.message : t("accounts.couldNotAddAccount"),
      });
    }
  };

  return (
    <FormProvider {...form}>
      <AccountLookupTabs
        errorKeyPrefix="add-sa"
        isSubmitting={isSubmitting}
        isValid={isValid}
        onAccountSubmit={form.handleSubmit(handleSubmit)}
        onSearchResults={handleSearchResults}
        onTabChange={setTabValue}
        renderAccountFields={() => (
          <>
            <AccountField<HCAddServiceAccountFormValues>
              {...FIELD_CONFIGS.nickname}
              name="nickname"
            />
            <AccountField<HCAddServiceAccountFormValues>
              {...FIELD_CONFIGS.nameOnAccount}
              name="nameOnAccount"
            />
            <AccountField<HCAddServiceAccountFormValues>
              {...FIELD_CONFIGS.externalId}
              name="externalId"
            />
            <AccountField<HCAddServiceAccountFormValues>
              {...FIELD_CONFIGS.zipCode}
              name="zipCode"
            />
          </>
        )}
        renderAddressResults={() =>
          hasResults ? (
            <div className="mt-4 flex gap-6">
              <div className="w-1/2 shrink-0">
                <AccountLookupResults
                  onSelect={handleAddressAccountSelected}
                  results={addressResults}
                />
              </div>
            </div>
          ) : null
        }
        rootError={rootError}
        showTabs={true}
        submitLabel={t("accounts.addAccount")}
        submittingLabel={t("accounts.adding")}
        tabValue={tabValue}
        tenantId={tenantId}
      />
    </FormProvider>
  );
}

export default observer(HCAddServiceAccountForm);
